import jsPDF from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';

// Apply the autoTable plugin to jsPDF
applyPlugin(jsPDF);

/**
 * SHARED PDF DOCUMENT BUILDER
 * Single source of truth for PDF generation
 * Returns the jsPDF document object for further processing
 */
const buildPDFDocument = (
  formData, 
  fsiBreakdown, 
  fsiCosts, 
  daChargesCosts, 
  societyCosts, 
  plData,
  competitorData = null
) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  // Brand Colors
  const colors = {
    primary: [30, 64, 175],      // Blue
    secondary: [100, 116, 139],  // Slate
    success: [34, 197, 94],      // Green
    warning: [245, 158, 11],     // Amber
    danger: [239, 68, 68],       // Red
    dark: [15, 23, 42],          // Slate 900
    light: [248, 250, 252],      // Slate 50
  };
  
  // ============ HELPER FUNCTIONS ============
  const formatCurrency = (value) => {
    if (!value || isNaN(value)) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };
  
  const formatCrores = (value) => {
    if (!value || isNaN(value)) return '₹0.00 Cr';
    const crores = value / 10000000;
    const formatted = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(crores);
    return `₹${formatted} Cr`;
  };

  // Short version for table cells — just the number, "₹ Cr" goes in header
  const fmtCr = (value) => {
    if (!value || isNaN(value)) return '0.00';
    const crores = value / 10000000;
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(crores);
  };
  
  const formatNumber = (value, decimals = 2) => {
    if (!value || isNaN(value)) return '0';
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  };

  const addSectionHeader = (title, yPos, color = colors.primary) => {
    doc.setFillColor(...color);
    doc.roundedRect(15, yPos, pageWidth - 30, 8, 1, 1, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text(title, 18, yPos + 5.5);
    // Reset to default for next content
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...colors.dark);
    return yPos + 12;
  };

  const checkPageBreak = (yPos, requiredSpace = 40) => {
    if (yPos + requiredSpace > pageHeight - 20) {
      doc.addPage();
      return 20;
    }
    return yPos;
  };

  // Shared table styles - matches page 3 (Development Potential) formatting
  const tableMargin = { left: 15, right: 15 };
  const baseTableStyles = {
    fontSize: 8,
    cellPadding: 3,
    lineColor: [220, 220, 220],
    lineWidth: 0.2,
    overflow: 'linebreak',
    valign: 'middle',
  };
  const headStyle = { fontSize: 9, fontStyle: 'bold' };
  const twoColStyles = () => ({
    0: { fontStyle: 'bold' },
    1: { halign: 'right', cellWidth: 65 },
  });
  const threeColStyles = () => ({
    0: { fontStyle: 'bold' },
    1: {},
    2: { halign: 'right', cellWidth: 65 },
  });

  // Mini pie chart helper
  const drawMiniPieChart = (x, y, data, radius = 15) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    if (total === 0) return;
    
    let startAngle = -Math.PI / 2;
    
    data.forEach((item) => {
      const sliceAngle = (item.value / total) * 2 * Math.PI;
      if (sliceAngle < 0.01) { startAngle += sliceAngle; return; }
      const endAngle = startAngle + sliceAngle;
      
      doc.setFillColor(...item.color);
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(0.3);
      
      // Fill slice using small triangles from center to arc
      const steps = Math.max(8, Math.ceil(sliceAngle / 0.05));
      for (let i = 0; i < steps; i++) {
        const a1 = startAngle + (sliceAngle * i / steps);
        const a2 = startAngle + (sliceAngle * (i + 1) / steps);
        doc.triangle(
          x, y,
          x + radius * Math.cos(a1), y + radius * Math.sin(a1),
          x + radius * Math.cos(a2), y + radius * Math.sin(a2),
          'F'
        );
      }
      
      // Draw border lines
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(0.5);
      doc.line(x, y, x + radius * Math.cos(startAngle), y + radius * Math.sin(startAngle));
      doc.line(x, y, x + radius * Math.cos(endAngle), y + radius * Math.sin(endAngle));
      
      startAngle = endAngle;
    });
  };

  // ============ HEADER ============
  doc.setFillColor(...colors.primary);
  doc.rect(0, 0, pageWidth, 35, 'F');
  
  // Title
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('REDEVELOPMENT FEASIBILITY REPORT', pageWidth / 2, 15, { align: 'center' });
  
  // Project name
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(formData.projectName || 'Untitled Project', pageWidth / 2, 23, { align: 'center' });
  
  doc.setFontSize(10);
  doc.setTextColor(...colors.light);
  doc.text(`${formData.projectMode === 'greenfield' ? (formData.projectName || 'JV Project') : (formData.societyName || 'Society')} | ${formData.location || 'Location'}`, pageWidth / 2, 30, { align: 'center' });
  
  let yPos = 45;
  
  // Report metadata with date and time
  doc.setFontSize(9);
  doc.setTextColor(...colors.secondary);
  const now = new Date();
  const dateTimeStr = now.toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }) + 
                      ' at ' + now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  doc.text(`Report Generated: ${dateTimeStr}`, 15, yPos);
  doc.text(`Classification: ${formData.projectClassification?.toUpperCase() || 'NON-TOD'}`, pageWidth - 15, yPos, { align: 'right' });
  
  yPos += 5;
  doc.setDrawColor(...colors.secondary);
  doc.setLineWidth(0.2);
  doc.line(15, yPos, pageWidth - 15, yPos);
  
  yPos += 10;
  
  // ============ SECTION 1: EXECUTIVE SUMMARY ============
  yPos = addSectionHeader('EXECUTIVE SUMMARY', yPos, colors.dark);
  yPos += 5;
  
  // Net Profit = Gross Profit (Bank Interest already deducted in Total Expenses)
  const netProfit = plData?.grossProfit || 0;
  const isProfitable = netProfit >= 0;
  
  // Summary boxes - NO viability badge
  const summaryData = [
    ['Total Revenue', formatCrores(plData?.totalRevenue), colors.success],
    ['Total Expenses', formatCrores(plData?.totalExpenses), colors.danger],
    ['Net Profit', formatCrores(netProfit), isProfitable ? colors.success : colors.danger],
    ['ROI', `${formatNumber(plData?.returnOnInvestment)}%`, isProfitable ? colors.success : colors.warning],
  ];
  
  const boxWidth = (pageWidth - 50) / 4;
  summaryData.forEach((item, index) => {
    const x = 15 + index * (boxWidth + 5);
    doc.setFillColor(...(index < 3 ? item[2] : [245, 247, 250]));
    doc.roundedRect(x, yPos, boxWidth, 20, 2, 2, 'F');
    
    doc.setFontSize(8);
    doc.setTextColor(index < 3 ? 255 : 100, index < 3 ? 255 : 116, index < 3 ? 255 : 139);
    doc.text(item[0], x + boxWidth/2, yPos + 7, { align: 'center' });
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(index < 3 ? 255 : 15, index < 3 ? 255 : 23, index < 3 ? 255 : 42);
    doc.text(item[1], x + boxWidth/2, yPos + 15, { align: 'center' });
    doc.setFont('helvetica', 'normal');
  });
  
  yPos += 28;
  
  // ============ PROJECT SNAPSHOT ============
  yPos = checkPageBreak(yPos, 35);
  
  const totalConstructedArea = fsiBreakdown?.totalFSIWithAncillary || 0;
  const saleableAreaSqft = plData?.finalSaleAreaSqft || 0;
  const sharingRatio = (plData?.societySharePercent > 0) 
    ? `${plData.societySharePercent.toFixed(0)}:${plData.developerSharePercent.toFixed(0)}` : '—';
  const initialInvestment = plData?.initialInvestment || 0;
  
  const quickStatsData = [
    ['Total Constructed Area', `${formatNumber(saleableAreaSqft > 0 ? (plData?.totalConstructedAreaSqft || 0) : 0, 0)} sft`],
    ['Area for Sale', `${formatNumber(saleableAreaSqft, 0)} sft`],
    ['Est. Flats for Sale', `${saleableAreaSqft > 0 ? Math.ceil(saleableAreaSqft / 1500) : 0}`],
    [plData?.isGreenfield ? 'JV Ratio (Landowner : Developer)' : 'Sharing Ratio (Society : Builder)', sharingRatio],
    ['Initial Investment', fmtCr(initialInvestment)],
    ...(plData?.initialInvestmentBasicMode != null ? [[
      'Initial Investment (Basic FSI 1.1 only)',
      fmtCr(plData.initialInvestmentBasicMode) +
        ((plData.initialInvestment - plData.initialInvestmentBasicMode) > 0
          ? `  (saves ${fmtCr(plData.initialInvestment - plData.initialInvestmentBasicMode)})`
          : '')
    ]] : []),
  ];
  
  doc.autoTable({
    startY: yPos,
    head: [['PROJECT SNAPSHOT', 'Value']],
    body: quickStatsData,
    theme: 'grid',
    headStyles: { fillColor: [30, 64, 175], ...headStyle },
    bodyStyles: { ...baseTableStyles },
    columnStyles: twoColStyles(),
    margin: tableMargin,
  });
  yPos = doc.lastAutoTable.finalY + 8;
  
  // ============ SECTION 2: PROJECT DETAILS (force to page 2) ============
  doc.addPage();
  yPos = 20;
  yPos = addSectionHeader('PROJECT DETAILS', yPos, colors.primary);
  yPos += 3;
  
  const projectDetails = [
    ['Project Type', formData.projectMode === 'greenfield' ? 'Greenfield (JV)' : 'Redevelopment'],
    ['Classification', formData.projectClassification?.toUpperCase() || 'NON-TOD'],
    ['Gross Plot Area', `${formatNumber(fsiBreakdown?.grossPlotArea, 2)} sq mtr`],
    ['Net Plot Area', `${formatNumber(fsiBreakdown?.netPlotArea, 2)} sq mtr`],
    ['Road Width', `${formData.roadWidth || 0} metres`],
    ['Total FSI (with Ancillary)', `${formatNumber(fsiBreakdown?.totalFSIWithAncillary, 2)} sq mtr`],
    ['Building Height', `${formData.buildingHeight || 24} metres`],
    ['Number of Members', formData.numMembers || 0],
    ['Project Duration', `${formData.projectDuration || 0} months`],
  ];
  
  // Existing Carpet Areas (only show what exists)
  if (formData.projectMode !== 'greenfield') {
    if (formData.hasFlats && parseFloat(formData.areaFlats) > 0) projectDetails.push(['Existing Flats Area', `${parseInt(formData.areaFlats).toLocaleString('en-IN')} sft`]);
    if (formData.hasOffices && parseFloat(formData.areaOffices) > 0) projectDetails.push(['Existing Offices Area', `${parseInt(formData.areaOffices).toLocaleString('en-IN')} sft`]);
    if (formData.hasShops && parseFloat(formData.areaShops) > 0) projectDetails.push(['Existing Shops Area', `${parseInt(formData.areaShops).toLocaleString('en-IN')} sft`]);
    if (parseFloat(formData.areaTerrace) > 0) projectDetails.push(['Terrace Area', `${parseInt(formData.areaTerrace).toLocaleString('en-IN')} sft`]);
    if (parseFloat(formData.areaGarden) > 0) projectDetails.push(['Garden Area', `${parseInt(formData.areaGarden).toLocaleString('en-IN')} sft`]);
    // Existing Parkings - right after existing areas
    const existingParkingsCount = parseInt(formData.existingParkings || 0);
    if (existingParkingsCount > 0) {
      projectDetails.push(['Existing Parkings (Total)', `${existingParkingsCount}`]);
    }
    const fourWheeler = parseInt(formData.fourWheelerPerMember || formData.fourWheelerParkingPerMember || 0);
    const twoWheeler = parseInt(formData.twoWheelerPerMember || formData.twoWheelerParkingPerMember || 0);
    if (fourWheeler > 0 || twoWheeler > 0) {
      projectDetails.push(['Offered Parking per Member (4W / 2W)', `${fourWheeler} / ${twoWheeler}`]);
    }
  }
  
  // Ready Reckoner Rates (applicable for both greenfield and redevelopment)
  projectDetails.push(['RR Rate (Plot)', formatCurrency(formData.rrRatePlot)]);
  projectDetails.push(['RR Rate (Flat)', formatCurrency(formData.rrRateFlat)]);
  if (formData.hasOffices && parseFloat(formData.rrRateOffice) > 0) {
    projectDetails.push(['RR Rate (Office)', formatCurrency(formData.rrRateOffice)]);
  }
  if (formData.hasShops && parseFloat(formData.rrRateShop) > 0) {
    projectDetails.push(['RR Rate (Shop)', formatCurrency(formData.rrRateShop)]);
  }
  projectDetails.push(['RR Multiplier', `${formData.rrMultiplier || 5}%`]);
  if (formData.applyRRMarkup === "yes") {
    projectDetails.push(['Additional RR Markup', `${formData.rrAdditionalMarkupPercent || 0}%`]);
  }
  
  // Society Terms
  if (formData.projectMode !== 'greenfield') {
    projectDetails.push(['', '']);
    projectDetails.push(['--- SOCIETY TERMS ---', '']);
    projectDetails.push(['Shifting Charges', `₹${parseInt(formData.shiftingCharges || 0).toLocaleString('en-IN')}/flat`]);
    projectDetails.push(['Corpus Charges', `₹${parseInt(formData.corpusCharges || 0).toLocaleString('en-IN')} (${formData.corpusChargesType === 'per_sft' ? 'Per Sft' : 'Per Flat'})`]);
    projectDetails.push(['Betterment Charges', `₹${parseInt(formData.bettermentCharges || 0).toLocaleString('en-IN')} (${formData.bettermentChargesType === 'per_sft' ? 'Per Sft' : 'Per Flat'})`]);
    projectDetails.push(['Rent (Flats/Offices)', `₹${parseInt(formData.rentPerSqftFlat || 0).toLocaleString('en-IN')}/sft/month`]);
    if (formData.hasShops && parseFloat(formData.rentPerSqftShop) > 0) projectDetails.push(['Rent (Shops)', `₹${parseInt(formData.rentPerSqftShop).toLocaleString('en-IN')}/sft/month`]);
    projectDetails.push(['Rent Escalation', `${formData.rentIncrementPA || 5}% p.a.`]);
    projectDetails.push(['Free Area %', `${formData.freeAreaPercentFlats || formData.additionalFreeAreaPercent || 0}%`]);
    projectDetails.push(['Bank Guarantee Amount', `₹${parseInt(formData.bankGuaranteeAmount || 0).toLocaleString('en-IN')}`]);
  }
  
  doc.autoTable({
    startY: yPos,
    head: [['Parameter', 'Value']],
    body: projectDetails,
    theme: 'grid',
    headStyles: { fillColor: colors.primary, ...headStyle },
    bodyStyles: { ...baseTableStyles },
    columnStyles: twoColStyles(),
    margin: tableMargin,
  });
  
  yPos = doc.lastAutoTable.finalY + 8;
  
  // ============ SECTION 4: DETAILED FSI BREAKDOWN ============
  yPos = checkPageBreak(yPos, 120);
  const isMhadaProject = formData.projectClassification === 'mhada';
  const isTodProject = formData.projectClassification === 'tod';
  const is6MeterRoad = fsiBreakdown?.is6MeterRoad || false;
  
  yPos = addSectionHeader('DEVELOPMENT POTENTIAL', yPos, isMhadaProject ? [139, 92, 246] : colors.success);
  yPos += 3;
  
  // Part A: Plot Area Details
  const plotAreaDetails = [
    ['PLOT AREA DETAILS', '', ''],
    ['Gross Plot Area', '', `${formatNumber(fsiBreakdown?.grossPlotArea, 2)} sq mtr`],
  ];
  
  // Road Widening Details (if applicable)
  const oldRoadWidening = fsiBreakdown?.oldRoadWidening || 0;
  const newRoadWidening = fsiBreakdown?.newRoadWidening || 0;
  const totalRoadWidening = fsiBreakdown?.totalRoadWidening || 0;
  
  if (totalRoadWidening > 0) {
    if (oldRoadWidening > 0) {
      plotAreaDetails.push(['   (-) Old Road Widening', 'TDR benefit only', `${formatNumber(oldRoadWidening, 2)} sq mtr`]);
    }
    if (newRoadWidening > 0) {
      plotAreaDetails.push(['   (-) New Road Widening', 'Reduces Net Plot', `${formatNumber(newRoadWidening, 2)} sq mtr`]);
    }
  }
  
  plotAreaDetails.push(['Net Plot Area (for FSI calc)', '', `${formatNumber(fsiBreakdown?.netPlotArea, 2)} sq mtr`]);
  plotAreaDetails.push(['Road Width', '', `${formData.roadWidth || 0} metres`]);
  plotAreaDetails.push(['Classification', '', isMhadaProject ? 'MHADA' : (isTodProject ? 'TOD' : 'Non-TOD')]);
  plotAreaDetails.push(['Permissible FSI', '', formatNumber(fsiBreakdown?.permissibleFSI, 2)]);
  
  // Part B: FSI Components
  plotAreaDetails.push(['', '', '']);
  plotAreaDetails.push(['FSI COMPONENTS', 'FSI Value', 'Area (sq mtr)']);
  plotAreaDetails.push(['1. Base FSI (1.1)', formatNumber(fsiBreakdown?.baseFSI, 2), formatNumber(fsiBreakdown?.basicFSIArea, 2)]);
  
  if (is6MeterRoad) {
    // 6 Meter Road - Special case
    plotAreaDetails.push(['   Note: 6m road - Limited FSI', '', '']);
    if (fsiBreakdown?.isOver30Years) {
      plotAreaDetails.push(['2. Incentive FSI (30+ years)', formatNumber(fsiBreakdown?.incentiveFSI, 2), formatNumber(fsiBreakdown?.incentiveFSIArea, 2)]);
    }
    plotAreaDetails.push(['   (No Paid FSI/TDR for 6m road)', '', '']);
  } else if (isMhadaProject) {
    // MHADA Project
    plotAreaDetails.push(['2. MHADA Incentive FSI', formatNumber(fsiBreakdown?.mhadaFSI || fsiBreakdown?.incentiveFSI, 2), formatNumber(fsiBreakdown?.mhadaFSIArea || fsiBreakdown?.incentiveFSIArea, 2)]);
    plotAreaDetails.push(['   (No TDR for MHADA)', '', '']);
  } else {
    // Standard FSI Calculation
    if (fsiBreakdown?.incentiveFSI > 0) {
      plotAreaDetails.push(['2. Incentive FSI (30+ years)', formatNumber(fsiBreakdown?.incentiveFSI, 2), formatNumber(fsiBreakdown?.incentiveFSIArea, 2)]);
    }
    plotAreaDetails.push(['3. Paid FSI', formatNumber(fsiBreakdown?.paidFSI, 2), formatNumber(fsiBreakdown?.paidFSIArea, 2)]);
    
    // TDR Details
    plotAreaDetails.push(['4. Total TDR', formatNumber(fsiBreakdown?.totalTDR, 2), formatNumber(fsiBreakdown?.tdrArea, 2)]);
    
    const amenityTDRPct = parseFloat(formData.amenityTDRPercent) || 70;
    const slumTDRPct = 100 - amenityTDRPct;
    plotAreaDetails.push([`   - Amenity TDR (${amenityTDRPct}%)`, formatNumber(fsiBreakdown?.amenityTDR, 2), formatNumber((fsiBreakdown?.amenityTDR || 0) * (fsiBreakdown?.netPlotArea || 0), 2)]);
    plotAreaDetails.push([`   - Slum TDR (${slumTDRPct}%)`, formatNumber(fsiBreakdown?.slumTDR, 2), formatNumber((fsiBreakdown?.slumTDR || 0) * (fsiBreakdown?.netPlotArea || 0), 2)]);
    
    // Road widening TDR reduction
    const tdrReduction = fsiBreakdown?.tdrReductionFromRoadWidening || 0;
    if (tdrReduction > 0) {
      plotAreaDetails.push(['', '', '']);
      plotAreaDetails.push(['TDR REDUCTION FROM ROAD WIDENING', '', '']);
      if (oldRoadWidening > 0) {
        plotAreaDetails.push([`   Old Road Widening (${formatNumber(oldRoadWidening, 0)} x 1)`, '', formatNumber(oldRoadWidening, 2)]);
      }
      if (newRoadWidening > 0) {
        plotAreaDetails.push([`   New Road Widening (${formatNumber(newRoadWidening, 0)} x 2)`, '', formatNumber(newRoadWidening * 2, 2)]);
      }
      plotAreaDetails.push(['   Total TDR Reduction', '', `${formatNumber(tdrReduction, 2)} sq mtr`]);
      plotAreaDetails.push(['   (Deducted from Amenity TDR only)', '', '']);
    }
    
    // Green FSI
    if (fsiBreakdown?.greenFSI > 0) {
      const greenCategory = formData.greenFSICategory || 'silver';
      const greenPct = greenCategory === 'platinum' ? '7%' : (greenCategory === 'gold' ? '5%' : '3%');
      plotAreaDetails.push(['5. Green Rating FSI (' + greenPct + ')', formatNumber(fsiBreakdown?.greenFSI, 2), formatNumber(fsiBreakdown?.greenFSIArea, 2)]);
    }
  }
  
  // Ancillary Section
  plotAreaDetails.push(['', '', '']);
  plotAreaDetails.push(['ANCILLARY FSI', '', '']);
  const resPct = parseFloat(formData.residentialPercentage) || 0;
  const comPct = 100 - resPct;
  plotAreaDetails.push([`   Residential Ancillary (60% x ${resPct}%)`, '', formatNumber(fsiBreakdown?.ancillaryResidential, 2)]);
  plotAreaDetails.push([`   Commercial Ancillary (80% x ${comPct}%)`, '', formatNumber(fsiBreakdown?.ancillaryCommercial, 2)]);
  plotAreaDetails.push(['   Total Ancillary', '', formatNumber(fsiBreakdown?.totalAncillary, 2)]);
  
  // Grand Total
  plotAreaDetails.push(['', '', '']);
  plotAreaDetails.push(['TOTAL BUILDABLE AREA', '', formatNumber(fsiBreakdown?.totalBuildableArea, 2)]);
  plotAreaDetails.push(['+ TOTAL ANCILLARY', '', formatNumber(fsiBreakdown?.totalAncillary, 2)]);
  plotAreaDetails.push(['= GRAND TOTAL FSI AREA', '', `${formatNumber(fsiBreakdown?.totalFSIWithAncillary, 2)} sq mtr`]);
  
  // Incentive FSI star note
  const hasIncentiveFSI = (fsiBreakdown?.incentiveFSI || 0) > 0;
  if (hasIncentiveFSI) {
    plotAreaDetails.push(['', '', '']);
    plotAreaDetails.push(['* Incentive FSI is applicable (30+ years old building).', '', '']);
    plotAreaDetails.push(['  Incentive FSI is adjusted against TDR purchase,', '', '']);
    plotAreaDetails.push(['  reducing the effective TDR area to be purchased.', '', '']);
  }
  
  doc.autoTable({
    startY: yPos,
    head: [['Description', 'FSI', 'Area']],
    body: plotAreaDetails,
    theme: 'grid',
    headStyles: { fillColor: isMhadaProject ? [139, 92, 246] : [22, 163, 74], ...headStyle },
    bodyStyles: { ...baseTableStyles },
    columnStyles: { 0: { fontStyle: 'bold' }, 1: { halign: 'right', cellWidth: 30 }, 2: { halign: 'right', cellWidth: 65 } },
    margin: tableMargin,
    didParseCell: (data) => {
      if (data.row.raw && data.row.raw[0] && data.row.raw[0].startsWith('* Incentive FSI')) {
        data.cell.styles.fontStyle = 'italic';
        data.cell.styles.textColor = [139, 92, 246];
        data.cell.styles.fontSize = 8;
      }
      // Style section headers
      if (data.row.raw && (
        data.row.raw[0] === 'PLOT AREA DETAILS' || 
        data.row.raw[0] === 'FSI COMPONENTS' ||
        data.row.raw[0] === 'TDR REDUCTION FROM ROAD WIDENING' ||
        data.row.raw[0] === 'ANCILLARY FSI'
      )) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [241, 245, 249];
      }
      // Style grand total
      if (data.row.raw && data.row.raw[0] === '= GRAND TOTAL FSI AREA') {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = isMhadaProject ? [245, 243, 255] : [220, 252, 231];
      }
      // Style net plot area and total buildable
      if (data.row.raw && (
        data.row.raw[0] === 'Net Plot Area (for FSI calc)' ||
        data.row.raw[0] === 'TOTAL BUILDABLE AREA'
      )) {
        data.cell.styles.fontStyle = 'bold';
      }
    }
  });
  
  yPos = doc.lastAutoTable.finalY + 8;
  
  // ============ SECTION 5: PMC CHARGES INCLUDING FSI ============
  yPos = checkPageBreak(yPos, 120);
  yPos = addSectionHeader('SANCTION CHARGES WITH FSI', yPos, [245, 158, 11]);
  yPos += 3;
  
  const rrMultPercent = parseFloat(formData.rrMultiplier) || 5;
  const rrMarkup = formData.applyRRMarkup === "yes" ? (parseFloat(formData.rrAdditionalMarkupPercent) || 0) : 0;
  const effectiveRRPlot = (parseFloat(formData.rrRatePlot) || 0) * (1 + rrMultPercent/100) * (1 + rrMarkup/100);
  
  const fsiCostCalcDetails = [];
  fsiCostCalcDetails.push(['RR Rate (Plot) Base', formatCurrency(formData.rrRatePlot), '']);
  if (rrMarkup > 0) {
    fsiCostCalcDetails.push(['+ RR Multiplier & Markup', `${rrMultPercent}% + ${rrMarkup}%`, `Effective: ${formatCurrency(effectiveRRPlot)}`]);
  } else {
    fsiCostCalcDetails.push(['+ RR Multiplier', `${rrMultPercent}%`, `Effective: ${formatCurrency(effectiveRRPlot)}`]);
  }
  fsiCostCalcDetails.push(['', '', '']);
  
  // Amenity TDR - detailed
  const amenityTDRArea = fsiBreakdown?.amenityTDRArea || 0;
  const amenityTDRPercent = parseFloat(formData.amenityTDRPercent) || 50;
  const adjustedAmenityTDRArea = fsiCosts?.adjustedAmenityTDRArea || 0;
  const originalAmenityArea = fsiCosts?.amenityTDRArea || amenityTDRArea;
  
  if (originalAmenityArea > 0) {
    fsiCostCalcDetails.push(['AMENITY TDR', '', '']);
    fsiCostCalcDetails.push(['  Original Area', '', `${formatNumber(originalAmenityArea, 2)} sq m`]);
    // Show incentive FSI deduction if applicable
    const incentiveFSI = fsiCosts?.incentiveFSI || 0;
    if (incentiveFSI > 0) {
      const incentiveDeduction = originalAmenityArea - (fsiCosts?.adjustedAmenityTDRArea || originalAmenityArea) - (fsiCosts?.tdrReductionFromRoadWidening || 0);
      if (Math.abs(incentiveDeduction) > 0.01) {
        fsiCostCalcDetails.push(['  (-) Incentive FSI Deduction', '', `${formatNumber(Math.abs(incentiveDeduction), 2)} sq m`]);
      }
    }
    // Show road widening deduction
    const tdrReduction = fsiCosts?.tdrReductionFromRoadWidening || 0;
    if (tdrReduction > 0) {
      fsiCostCalcDetails.push(['  (-) Road Widening Deduction', '', `${formatNumber(tdrReduction, 2)} sq m`]);
    }
    fsiCostCalcDetails.push(['  Final Amenity TDR Area', '', `${formatNumber(adjustedAmenityTDRArea, 2)} sq m`]);
    fsiCostCalcDetails.push(['  Cost', `${formatNumber(adjustedAmenityTDRArea, 0)} sq m x ₹${formatNumber(effectiveRRPlot, 0)} x ${amenityTDRPercent}%`, fmtCr(fsiCosts?.amenityTDRCost)]);
  }
  
  fsiCostCalcDetails.push(['', '', '']);
  
  // Slum TDR - detailed
  const slumTDRArea = fsiBreakdown?.slumTDRArea || 0;
  const adjustedSlumTDRArea = fsiCosts?.adjustedSlumTDRArea || slumTDRArea;
  const originalSlumArea = fsiCosts?.slumTDRArea || slumTDRArea;
  
  if (originalSlumArea > 0) {
    fsiCostCalcDetails.push(['SLUM TDR', '', '']);
    fsiCostCalcDetails.push(['  Original Area', '', `${formatNumber(originalSlumArea, 2)} sq m`]);
    const incentiveFSI = fsiCosts?.incentiveFSI || 0;
    if (incentiveFSI > 0) {
      const slumIncentiveDeduction = originalSlumArea - adjustedSlumTDRArea;
      if (Math.abs(slumIncentiveDeduction) > 0.01) {
        fsiCostCalcDetails.push(['  (-) Incentive FSI Deduction', '', `${formatNumber(Math.abs(slumIncentiveDeduction), 2)} sq m`]);
      }
    }
    fsiCostCalcDetails.push(['  Final Slum TDR Area', '', `${formatNumber(adjustedSlumTDRArea, 2)} sq m`]);
    const slumRate = parseFloat(formData.slumTDRZoneDRC) || effectiveRRPlot;
    const slumTDRPercent = parseFloat(formData.slumTDRPercent) || 100;
    if (fsiCosts?.slumTDRMethod === "zone_drc") {
      const zoneDRCRate = parseFloat(formData.slumTDRZoneDRC) || 0;
      fsiCostCalcDetails.push(['  Cost', `${formatNumber(adjustedSlumTDRArea, 0)} sq m x Zone DRC ₹${formatNumber(zoneDRCRate, 0)}/sq m`, fmtCr(fsiCosts?.slumTDRCost)]);
    } else {
      fsiCostCalcDetails.push(['  Cost', `${formatNumber(adjustedSlumTDRArea, 0)} sq m x ₹${formatNumber(slumRate, 0)} x ${slumTDRPercent}%`, fmtCr(fsiCosts?.slumTDRCost)]);
    }
  }
  
  fsiCostCalcDetails.push(['', '', '']);
  
  // Paid FSI
  const paidFSIArea = fsiCosts?.adjustedPaidFSIArea || fsiBreakdown?.paidFSIArea || 0;
  const paidFSIPercent = parseFloat(formData.paidFSIPercent) || 35;
  if (paidFSIArea > 0) {
    fsiCostCalcDetails.push(['Paid FSI', `${formatNumber(paidFSIArea, 0)} sq m x ₹${formatNumber(effectiveRRPlot, 0)} x ${paidFSIPercent}%`, fmtCr(fsiCosts?.paidFSICost)]);
  }
  
  // Ancillary
  const ancillaryArea = fsiBreakdown?.totalAncillary || 0;
  const ancillaryPercent = parseFloat(formData.ancillaryFSIPercent) || 15;
  if (ancillaryArea > 0) {
    fsiCostCalcDetails.push(['Ancillary FSI', `${formatNumber(ancillaryArea, 0)} sq m x ₹${formatNumber(effectiveRRPlot, 0)} x ${ancillaryPercent}%`, fmtCr(fsiCosts?.ancillaryFSICost)]);
  }
  
  // PMC Charges - summary only (no breakup)
  const pmcChargesTotal = (fsiCosts?.landDevCharges || 0) + (fsiCosts?.buildingDevCharges || 0) + 
                          (fsiCosts?.upkar || 0) + (fsiCosts?.fireFightingCharges || 0) +
                          (fsiCosts?.scrutinyCharges || 0) + (fsiCosts?.radaRodaCharges || 0) +
                          (fsiCosts?.demolitionCharges || 0) + (fsiCosts?.waterLineCharges || 0) +
                          (fsiCosts?.cdCharges || 0) + (fsiCosts?.nonFSICharges || 0);
  
  fsiCostCalcDetails.push(['', '', '']);
  fsiCostCalcDetails.push(['SNA / Sanction Charges', '(Dev + Upkar + Fire + Other)', fmtCr(pmcChargesTotal)]);
  
  if ((fsiCosts?.ulcCharges || 0) > 0) {
    fsiCostCalcDetails.push(['ULC Sec 20 Charges', '', fmtCr(fsiCosts?.ulcCharges)]);
  }
  
  fsiCostCalcDetails.push(['', '', '']);
  fsiCostCalcDetails.push(['TOTAL SANCTION CHARGES WITH FSI', '', fmtCr(fsiCosts?.totalCosts)]);
  
  doc.autoTable({
    startY: yPos,
    head: [['Component', 'Calculation', '₹ Cr']],
    body: fsiCostCalcDetails,
    theme: 'grid',
    headStyles: { fillColor: [245, 158, 11], ...headStyle },
    bodyStyles: { ...baseTableStyles },
    columnStyles: { 0: { fontStyle: 'bold' }, 1: {}, 2: { halign: 'right', cellWidth: 65 } },
    margin: tableMargin,
    didParseCell: (data) => {
      if (data.row.raw && data.row.raw[0] === 'TOTAL SANCTION CHARGES WITH FSI') {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [254, 243, 199];
      }
      if (data.row.raw && (data.row.raw[0] === 'AMENITY TDR' || data.row.raw[0] === 'SLUM TDR')) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [241, 245, 249];
      }
    }
  });
  
  yPos = doc.lastAutoTable.finalY + 8;
  
  // ============ SECTION 6: DA CHARGES ============
  yPos = checkPageBreak(yPos, 60);
  yPos = addSectionHeader('DA CHARGES', yPos, colors.secondary);
  yPos += 3;
  
  const daDetails = [
    ['Stamp Duty', fmtCr(daChargesCosts?.totalStampDuty)],
    ['Registration', formatCurrency(daChargesCosts?.totalRegistration || 30000)],
    ['TOTAL DA CHARGES', fmtCr(daChargesCosts?.totalDACharges)],
  ];
  
  doc.autoTable({
    startY: yPos,
    head: [['Charge Type', '₹ Cr']],
    body: daDetails,
    theme: 'grid',
    headStyles: { fillColor: colors.secondary, ...headStyle },
    bodyStyles: { ...baseTableStyles },
    columnStyles: twoColStyles(),
    margin: tableMargin,
    didParseCell: (data) => {
      if (data.row.raw && data.row.raw[0] === 'TOTAL DA CHARGES') {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [241, 245, 249];
      }
    }
  });
  
  yPos = doc.lastAutoTable.finalY + 8;
  
  // ============ SECTION 7: SOCIETY EXPENSES ============
  if (formData.projectMode !== 'greenfield') {
    yPos = checkPageBreak(yPos, 80);
    yPos = addSectionHeader('SOCIETY EXPENSES', yPos, colors.primary);
    yPos += 3;
    
    const societyDetails = [
      ['Shifting Charges', fmtCr(societyCosts?.shiftingCharges)],
      ['Corpus Charges', fmtCr(societyCosts?.corpusCharges)],
      ['Betterment Charges', fmtCr(societyCosts?.bettermentCharges)],
      ['Rental Cost (Total)', fmtCr(societyCosts?.totalRentalCost)],
      ['Brokerage Cost', fmtCr(societyCosts?.brokerageCost)],
      ['SNA Cost', fmtCr(societyCosts?.pmcCost)],
      ['Bank Guarantee Cost', fmtCr(societyCosts?.bgCost)],
      ['GST (Total)', fmtCr(societyCosts?.totalGST)],
      ['TOTAL SOCIETY EXPENSES', fmtCr(societyCosts?.totalSocietyExpenses)],
    ];
    
    doc.autoTable({
      startY: yPos,
      head: [['Expense Type', '₹ Cr']],
      body: societyDetails,
      theme: 'grid',
      headStyles: { fillColor: colors.primary, ...headStyle },
      bodyStyles: { ...baseTableStyles },
      columnStyles: twoColStyles(),
      margin: tableMargin,
      didParseCell: (data) => {
        if (data.row.raw && data.row.raw[0] === 'TOTAL SOCIETY EXPENSES') {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [219, 234, 254];
        }
      }
    });
    
    yPos = doc.lastAutoTable.finalY + 8;
  }
  
  // ============ SECTION 8: CONSTRUCTION & OTHER COSTS ============
  yPos = checkPageBreak(yPos, 80);
  yPos = addSectionHeader('CONSTRUCTION & OTHER COSTS', yPos, colors.secondary);
  yPos += 3;
  
  const constructionOtherCosts = [
    ['Total Constructed Area', `${formatNumber(plData?.totalConstructedAreaSqft, 0)} sft`],
    ['Construction Rate', formatCurrency(plData?.constructionCostPerSqft) + '/sft'],
    ['Total Construction Cost', fmtCr(plData?.totalConstructionCost)],
    ['', ''],
    ['Marketing Cost', fmtCr(plData?.totalMarketingCost)],
    ['Miscellaneous Expenses', fmtCr(plData?.totalMiscExp)],
    ['Brokerage Commission', fmtCr(plData?.totalBrokerageCommission)],
    ['Total Other Costs', fmtCr(plData?.totalOtherCosts)],
  ];
  
  doc.autoTable({
    startY: yPos,
    head: [['Item', '₹ Cr']],
    body: constructionOtherCosts,
    theme: 'grid',
    headStyles: { fillColor: colors.secondary, ...headStyle },
    bodyStyles: { ...baseTableStyles },
    columnStyles: twoColStyles(),
    margin: tableMargin,
    didParseCell: (data) => {
      if (data.row.raw && (data.row.raw[0] === 'Total Construction Cost' || data.row.raw[0] === 'Total Other Costs')) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [240, 240, 240];
      }
    }
  });
  
  yPos = doc.lastAutoTable.finalY + 8;
  
  // ============ SECTION: BANK INTEREST ============
  yPos = checkPageBreak(yPos, 70);
  yPos = addSectionHeader('BANK INTEREST', yPos, colors.warning);
  yPos += 3;
  
  const projectInterestRate = formData.projectInterestRate !== undefined && formData.projectInterestRate !== "" 
    ? parseFloat(formData.projectInterestRate) : 12;
  const durationYears = Math.ceil((parseFloat(formData.projectDuration) || 36) / 12);
  
  const bankInterestDetails = [
    ['Interest Part A (80% of FSI Costs @ 8% p.a.)', fmtCr(plData?.interestPartA)],
    ['Interest Part B (Project Costs @ ' + projectInterestRate + '% p.a.)', fmtCr(plData?.interestPartB)],
    ['Duration', `${durationYears} years`],
    ['TOTAL BANK INTEREST', fmtCr(plData?.totalBankInterest)],
  ];
  
  doc.autoTable({
    startY: yPos,
    head: [['Component', '₹ Cr']],
    body: bankInterestDetails,
    theme: 'grid',
    headStyles: { fillColor: colors.warning, ...headStyle },
    bodyStyles: { ...baseTableStyles },
    columnStyles: twoColStyles(),
    margin: tableMargin,
    didParseCell: (data) => {
      if (data.row.raw && data.row.raw[0] === 'TOTAL BANK INTEREST') {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [255, 243, 205];
      }
    }
  });
  
  yPos = doc.lastAutoTable.finalY + 8;
  
  // ============ SECTION 9: INITIAL INVESTMENT REQUIRED ============
  yPos = checkPageBreak(yPos, 80);
  yPos = addSectionHeader('INITIAL INVESTMENT REQUIRED', yPos, colors.warning);
  yPos += 3;
  
  // Betterment is NOT part of initial investment - paid at possession
  
  const investmentDetails = [
    ['TDR Cost (Amenity + Slum)', fmtCr(plData?.tdrCost)],
    ['Paid FSI + Ancillary (20%, min ₹50L)', fmtCr(plData?.minPaidFSIInvestment || 0)],
    ['Other Sanction Costs', fmtCr(plData?.sanctionCostsExTDRPaid || 0)],
    ['DA Charges', fmtCr(daChargesCosts?.totalDACharges)],
    ['Shifting Charges (50%)', fmtCr(plData?.shiftingInInitial || 0)],
    ...(plData?.bettermentOnVacation > 0 ? [['Betterment (50% on vacation)', fmtCr(plData?.bettermentOnVacation)]] : []),
    ...(plData?.depositCost > 0 ? [['Deposit (' + (formData?.depositMonths || 0) + ' months)', fmtCr(plData?.depositCost)]] : []),
    ...(plData?.brokerageCostSociety > 0 ? [['Brokerage', fmtCr(plData?.brokerageCostSociety)]] : []),
    ...(plData?.bgBaseAmount > 0 ? [['Bank Guarantee (Full Amount)', fmtCr(plData?.bgBaseAmount)]] : []),
    ...((plData?.ulcCharges || 0) > 0 ? [['ULC Sec 20 Charges', fmtCr(plData?.ulcCharges)]] : []),
    ['TOTAL INITIAL INVESTMENT', fmtCr(plData?.initialInvestment)],
    ...(plData?.initialInvestmentBasicMode != null ? [[
      'Alternate — Basic FSI 1.1 Sanction Only' +
        ((plData.initialInvestment - plData.initialInvestmentBasicMode) > 0
          ? ` (saves ${fmtCr(plData.initialInvestment - plData.initialInvestmentBasicMode)})`
          : ''),
      fmtCr(plData.initialInvestmentBasicMode)
    ]] : []),
  ];
  
  doc.autoTable({
    startY: yPos,
    head: [['Investment Component', '₹ Cr']],
    body: investmentDetails,
    theme: 'grid',
    headStyles: { fillColor: colors.warning, ...headStyle },
    bodyStyles: { ...baseTableStyles },
    columnStyles: twoColStyles(),
    margin: tableMargin,
    didParseCell: (data) => {
      if (data.row.raw && data.row.raw[0] === 'TOTAL INITIAL INVESTMENT') {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [254, 243, 199];
      }
      if (data.row.raw && typeof data.row.raw[0] === 'string' && data.row.raw[0].startsWith('Alternate — Basic FSI')) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [209, 250, 229]; // emerald-100
        data.cell.styles.textColor = [4, 120, 87];    // emerald-700
      }
    }
  });
  
  yPos = doc.lastAutoTable.finalY + 8;
  
  // ============ SECTION 9: REVENUE ANALYSIS ============
  yPos = checkPageBreak(yPos, 80);
  yPos = addSectionHeader('REVENUE PROJECTION', yPos, colors.success);
  yPos += 3;
  
  const revenueDetails = [
    ['Sale Rate (Weighted Avg)', formatCurrency(plData?.saleRatePerSqft) + '/sft'],
    ['Final Sale Area', `${formatNumber(plData?.finalSaleAreaSqft, 0)} sft`],
    ['TOTAL SALE REVENUE', fmtCr(plData?.totalSaleRevenue)],
  ];
  
  doc.autoTable({
    startY: yPos,
    head: [['Metric', 'Value']],
    body: revenueDetails,
    theme: 'grid',
    headStyles: { fillColor: colors.success, ...headStyle },
    bodyStyles: { ...baseTableStyles },
    columnStyles: twoColStyles(),
    margin: tableMargin,
    didParseCell: (data) => {
      if (data.row.raw && data.row.raw[0] === 'TOTAL SALE REVENUE') {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [220, 252, 231];
      }
    }
  });
  
  yPos = doc.lastAutoTable.finalY + 8;
  
  // ============ SECTION 13: FINANCIAL SUMMARY ============
  yPos = checkPageBreak(yPos, 100);
  yPos = addSectionHeader('FINANCIAL SUMMARY', yPos, isProfitable ? colors.success : colors.danger);
  yPos += 3;
  
  const plSummary = [
    ['TOTAL REVENUE', fmtCr(plData?.totalRevenue)],
    ['', ''],
    ['FSI & Sanction Costs', fmtCr(fsiCosts?.totalCosts)],
    ['DA Charges', fmtCr(daChargesCosts?.totalDACharges)],
    ['Society Expenses', fmtCr(plData?.totalSocietyExpenses)],
    ['Construction Cost', fmtCr(plData?.totalConstructionCost)],
    ['Other Costs (Marketing + Misc)', fmtCr(plData?.totalOtherCosts)],
    ['Bank Interest', fmtCr(plData?.totalBankInterest)],
    ['TOTAL EXPENSES', fmtCr(plData?.totalExpenses)],
    ...(plData?.fsiTotalCostsBasicMode != null && plData.fsiTotalCosts > plData.fsiTotalCostsBasicMode
      ? [['Alt. scenario (Basic FSI 1.1 only) — cost savings', fmtCr(plData.fsiTotalCosts - plData.fsiTotalCostsBasicMode)]]
      : []),
    ['', ''],
    ['NET PROFIT', fmtCr(netProfit)],
    ['Profit Margin', `${formatNumber(plData?.profitMarginPercent)}%`],
    ['Return on Investment (ROI)', `${formatNumber(plData?.returnOnInvestment)}%`],
  ];
  
  doc.autoTable({
    startY: yPos,
    head: [['Item', '₹ Cr']],
    body: plSummary,
    theme: 'grid',
    headStyles: { fillColor: isProfitable ? colors.success : colors.danger, ...headStyle },
    bodyStyles: { ...baseTableStyles },
    columnStyles: twoColStyles(),
    margin: tableMargin,
    didParseCell: (data) => {
      if (data.row.raw) {
        if (data.row.raw[0] === 'NET PROFIT') {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = isProfitable ? [220, 252, 231] : [254, 226, 226];
          data.cell.styles.textColor = isProfitable ? [22, 101, 52] : [153, 27, 27];
        }
        if (data.row.raw[0] === 'TOTAL REVENUE' || data.row.raw[0] === 'TOTAL EXPENSES') {
          data.cell.styles.fontStyle = 'bold';
        }
      }
    }
  });
  
  yPos = doc.lastAutoTable.finalY + 8;
  
  // ============ SECTION 15: SENSITIVITY ANALYSIS KEY INSIGHTS ============
  yPos = checkPageBreak(yPos, 120);
  yPos = addSectionHeader('SENSITIVITY ANALYSIS', yPos, [139, 92, 246]);
  yPos += 3;
  
  const baseSaleRate = parseFloat(formData.saleRateFlats) || parseFloat(formData.saleRatePerSqft) || 0;
  const baseFreeArea = parseFloat(formData.freeAreaPercentFlats) || parseFloat(formData.additionalFreeAreaPercent) || 0;
  const baseCCost = parseFloat(formData.constructionCostPerSqft) || 0;
  const baseProfit = plData?.grossProfit || 0;
  
  // Sale Rate Sensitivity (in multiples of ₹500)
  const saleRateVariations = [-1500, -1000, -500, 0, 500, 1000, 1500];
  const sensitivityData = saleRateVariations.map(delta => {
    const rate = baseSaleRate + delta;
    const revenue = (plData?.finalSaleAreaSqft || 0) * rate;
    const profit = revenue - (plData?.totalExpenses || 0);
    const margin = revenue > 0 ? (profit / revenue * 100) : 0;
    return [delta === 0 ? 'Base' : `${delta > 0 ? '+' : ''}₹${delta}`, `₹${formatNumber(rate, 0)}/sft`, fmtCr(profit), `${margin.toFixed(1)}%`];
  });
  
  doc.autoTable({
    startY: yPos,
    head: [['Change', 'Sale Rate', 'Profit (₹ Cr)', 'Margin']],
    body: sensitivityData,
    theme: 'grid',
    headStyles: { fillColor: [139, 92, 246], ...headStyle, textColor: [255, 255, 255] },
    bodyStyles: { ...baseTableStyles },
    columnStyles: { 0: { halign: 'center' }, 1: {}, 2: { halign: 'right', cellWidth: 50 }, 3: { halign: 'center', cellWidth: 30 } },
    margin: tableMargin,
    didParseCell: (data) => {
      if (data.row.raw && data.row.raw[0] === 'Base') {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [219, 234, 254];
      }
    }
  });
  
  yPos = doc.lastAutoTable.finalY + 8;
  
  // Free Area Sensitivity
  yPos = checkPageBreak(yPos, 80);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Free Area % Sensitivity', 15, yPos);
  doc.setFont('helvetica', 'normal');
  yPos += 3;
  
  const freeAreaVariations = [-5, -2, 0, 2, 5, 10];
  const freeAreaSensData = freeAreaVariations.map(delta => {
    const newFreeArea = Math.max(0, baseFreeArea + delta);
    const convFactor = 0.092903;
    const existArea = (plData?.existingCarpetArea || 0);
    const addFree = existArea * (newFreeArea / 100);
    const terraceGarden = (plData?.convertedTerraceGarden || 0) + (plData?.incrementOnTerrace || 0) + (plData?.incrementOnGarden || 0);
    const parkingCarpet = formData.parkingCompensationType === "carpet" ? (parseFloat(formData.compensationCarpetArea) || 0) * convFactor : 0;
    const totalFree = addFree + terraceGarden + parkingCarpet;
    const balance = Math.max(0, (plData?.totalCarpetArea || 0) - existArea - totalFree);
    const loadPct = parseFloat(formData.loadingPercentage) || 40;
    const saleableArea = (balance * (1 + loadPct / 100)) / convFactor;
    const revenue = saleableArea * (plData?.saleRatePerSqft || baseSaleRate);
    const profit = revenue - (plData?.totalExpenses || 0);
    return [`${newFreeArea.toFixed(0)}%`, `${delta >= 0 ? '+' : ''}${delta}%`, fmtCr(profit)];
  });
  
  doc.autoTable({
    startY: yPos,
    head: [['Free Area %', 'Change', 'Profit (₹ Cr)']],
    body: freeAreaSensData,
    theme: 'grid',
    headStyles: { fillColor: [139, 92, 246], ...headStyle, textColor: [255, 255, 255] },
    bodyStyles: { ...baseTableStyles },
    columnStyles: { 0: { halign: 'center' }, 1: { halign: 'center' }, 2: { halign: 'right', cellWidth: 50 } },
    margin: tableMargin,
  });
  
  yPos = doc.lastAutoTable.finalY + 8;
  
  // Construction Cost Sensitivity
  yPos = checkPageBreak(yPos, 80);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...colors.dark);
  doc.text('Construction Cost Sensitivity', 15, yPos);
  doc.setFont('helvetica', 'normal');
  yPos += 3;
  
  const ccVariations = [-200, -100, 0, 100, 200];
  const ccSensData = ccVariations.map(delta => {
    const newCC = baseCCost + delta;
    const totalConstructedAreaSqft = plData?.totalConstructedAreaSqft || 0;
    const costDiff = delta * totalConstructedAreaSqft;
    const profit = baseProfit - costDiff;
    const profitChange = profit - baseProfit;
    return [
      delta === 0 ? 'Base' : `${delta > 0 ? '+' : ''}₹${delta}`,
      `₹${formatNumber(newCC, 0)}/sft`,
      fmtCr(profit),
      delta === 0 ? '-' : `${profitChange >= 0 ? '+' : ''}${fmtCr(profitChange)}`
    ];
  });
  
  doc.autoTable({
    startY: yPos,
    head: [['Change', 'Cost/sft', 'Profit (₹ Cr)', 'Impact']],
    body: ccSensData,
    theme: 'grid',
    headStyles: { fillColor: [139, 92, 246], ...headStyle, textColor: [255, 255, 255] },
    bodyStyles: { ...baseTableStyles },
    columnStyles: { 0: { halign: 'center' }, 1: {}, 2: { halign: 'right', cellWidth: 50 }, 3: { halign: 'right', cellWidth: 50 } },
    margin: tableMargin,
    didParseCell: (data) => {
      if (data.row.raw && data.row.raw[0] === 'Base') {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [219, 234, 254];
      }
    }
  });
  
  yPos = doc.lastAutoTable.finalY + 8;
  
  // Key Sensitivity Insights
  yPos = checkPageBreak(yPos, 70);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...colors.dark);
  doc.text('Key Insights', 15, yPos);
  doc.setFont('helvetica', 'normal');
  yPos += 3;
  
  const saleImpact500 = ((plData?.finalSaleAreaSqft || 0) * 500) / 10000000;
  const ccImpact100 = ((plData?.totalConstructedAreaSqft || 0) * 100) / 10000000;
  const freeArea1pctImpact = (() => {
    const convFactor = 0.092903;
    const existArea = plData?.existingCarpetArea || 0;
    const addFreeBase = existArea * (baseFreeArea / 100);
    const addFree1 = existArea * ((baseFreeArea + 1) / 100);
    const extraFreeArea = addFree1 - addFreeBase;
    const loadPct = parseFloat(formData.loadingPercentage) || 40;
    const lostSaleableArea = (extraFreeArea * (1 + loadPct / 100)) / convFactor;
    return (lostSaleableArea * (plData?.saleRatePerSqft || baseSaleRate)) / 10000000;
  })();
  const breakEvenRate = (plData?.totalExpenses || 0) / (plData?.finalSaleAreaSqft || 1);
  
  const insightsData = [
    ['Sale Rate Impact', `Every ₹500/sft increase adds ~₹${saleImpact500.toFixed(2)} Cr to profit`],
    ['Free Area Impact', `Each 1% increase in free area reduces profit by ~₹${freeArea1pctImpact.toFixed(2)} Cr`],
    ['Construction Cost Impact', `Every ₹100/sft increase reduces profit by ~₹${ccImpact100.toFixed(2)} Cr`],
    ['Break-even Sale Rate', `₹${formatNumber(breakEvenRate, 0)}/sft (to cover all expenses)`],
    ['Cost per Saleable Sft', `₹${formatNumber((plData?.totalExpenses || 0) / (plData?.finalSaleAreaSqft || 1), 0)}/sft`],
    ['Profit per Saleable Sft', `₹${formatNumber((plData?.grossProfit || 0) / (plData?.finalSaleAreaSqft || 1), 0)}/sft`],
  ];
  
  doc.autoTable({
    startY: yPos,
    head: [['Factor', 'Insight']],
    body: insightsData,
    theme: 'grid',
    headStyles: { fillColor: [139, 92, 246], ...headStyle, textColor: [255, 255, 255] },
    bodyStyles: { ...baseTableStyles },
    columnStyles: { 0: { fontStyle: 'bold' } },
    margin: tableMargin,
  });
  
  yPos = doc.lastAutoTable.finalY + 8;
  
  // ============ SECTION 16: COST, AREA & REVENUE BREAKDOWN ============
  yPos = checkPageBreak(yPos, 120);
  yPos = addSectionHeader('COST, AREA & REVENUE BREAKDOWN', yPos, [20, 184, 166]);
  yPos += 3;
  
  // --- COST BREAKDOWN with Pie Chart ---
  const totalExpenses = plData?.totalExpenses || 1;
  const costPieData = [];
  const costColors = [[30, 64, 175], [245, 158, 11], [139, 92, 246], [34, 197, 94], [239, 68, 68], [20, 184, 166], [100, 116, 139]];
  if ((fsiCosts?.totalCosts || 0) > 0) costPieData.push({ label: 'FSI & Sanction', value: fsiCosts.totalCosts, color: costColors[0] });
  if ((daChargesCosts?.totalDACharges || 0) > 0) costPieData.push({ label: 'DA Charges', value: daChargesCosts.totalDACharges, color: costColors[1] });
  if ((plData?.totalSocietyExpenses || 0) > 0) costPieData.push({ label: 'Society', value: plData.totalSocietyExpenses, color: costColors[2] });
  if ((plData?.totalConstructionCost || 0) > 0) costPieData.push({ label: 'Construction', value: plData.totalConstructionCost, color: costColors[3] });
  if ((plData?.totalOtherCosts || 0) > 0) costPieData.push({ label: 'Other Costs', value: plData.totalOtherCosts, color: costColors[4] });
  if ((plData?.totalBankInterest || 0) > 0) costPieData.push({ label: 'Bank Interest', value: plData.totalBankInterest, color: costColors[5] });
  if ((plData?.pmcCost || 0) > 0) costPieData.push({ label: 'SNA', value: plData.pmcCost, color: costColors[6] });
  
  // Draw pie chart for cost breakdown
  drawMiniPieChart(40, yPos + 18, costPieData, 16);
  
  // Legend for cost pie chart
  let legendY = yPos + 2;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...colors.dark);
  doc.text('Cost Breakdown', 65, legendY);
  legendY += 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  const costTotal = costPieData.reduce((s, i) => s + i.value, 0);
  costPieData.forEach((item) => {
    doc.setFillColor(...item.color);
    doc.rect(65, legendY - 2.5, 3, 3, 'F');
    const pct = costTotal > 0 ? ((item.value / costTotal) * 100).toFixed(1) : '0.0';
    doc.setTextColor(...colors.dark);
    doc.text(`${item.label}: ${pct}%`, 70, legendY);
    legendY += 4;
  });
  
  yPos += 40;
  
  // --- AREA DISTRIBUTION with Pie Chart (chart only, no table) ---
  yPos = checkPageBreak(yPos, 60);
  
  const totalCarpet = plData?.totalCarpetArea || 1;
  const areaPieData = [
    { label: 'Existing', value: plData?.existingCarpetArea || 0, color: [30, 64, 175] },
    { label: 'Free Area', value: plData?.totalFreeArea || 0, color: [245, 158, 11] },
    { label: 'Developer', value: plData?.balanceCarpetArea || 0, color: [34, 197, 94] },
  ];
  
  // Draw pie chart for area distribution
  drawMiniPieChart(40, yPos + 18, areaPieData, 16);
  
  // Legend for area pie chart
  let areaLegendY = yPos + 5;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...colors.dark);
  doc.text('Area Distribution', 65, areaLegendY);
  areaLegendY += 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  areaPieData.forEach((item) => {
    doc.setFillColor(...item.color);
    doc.rect(65, areaLegendY - 2.5, 3, 3, 'F');
    const pct = totalCarpet > 0 ? ((item.value / totalCarpet) * 100).toFixed(1) : '0.0';
    doc.setTextColor(...colors.dark);
    doc.text(`${item.label}: ${formatNumber(item.value, 2)} sqm (${pct}%)`, 70, areaLegendY);
    areaLegendY += 4;
  });
  
  yPos += 40;
  
  // --- REVENUE BREAKDOWN with Pie Chart (chart only, no table) ---
  yPos = checkPageBreak(yPos, 60);
  
  const convFactor = 0.092903;
  const exFlats = (parseFloat(formData.areaFlats) || 0) * convFactor;
  const exOffices = (parseFloat(formData.areaOffices) || 0) * convFactor;
  const exShops = (parseFloat(formData.areaShops) || 0) * convFactor;
  const totalEx = exFlats + exOffices + exShops;
  const totRev = plData?.totalRevenue || 1;
  
  const revPieData = [];
  const revColors = [[34, 197, 94], [30, 64, 175], [245, 158, 11]];
  if (exFlats > 0) revPieData.push({ label: 'Flats', value: totRev * (exFlats / totalEx), color: revColors[0] });
  if (exOffices > 0) revPieData.push({ label: 'Offices', value: totRev * (exOffices / totalEx), color: revColors[1] });
  if (exShops > 0) revPieData.push({ label: 'Shops', value: totRev * (exShops / totalEx), color: revColors[2] });
  
  if (revPieData.length > 1) {
    // Draw pie chart for revenue breakdown (only if multiple types)
    drawMiniPieChart(40, yPos + 18, revPieData, 16);
    
    // Legend for revenue pie chart
    let revLegendY = yPos + 5;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...colors.dark);
    doc.text('Revenue by Property Type', 65, revLegendY);
    revLegendY += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    revPieData.forEach((item) => {
      doc.setFillColor(...item.color);
      doc.rect(65, revLegendY - 2.5, 3, 3, 'F');
      const pct = totRev > 0 ? ((item.value / totRev) * 100).toFixed(1) : '0.0';
      doc.setTextColor(...colors.dark);
      doc.text(`${item.label}: ${formatCrores(item.value)} (${pct}%)`, 70, revLegendY);
      revLegendY += 4;
    });
    
    yPos += 40;
  }
  
  yPos += 3;
  
  // ============ SECTION 17: COMPETITOR COMPARISON (if available) ============
  if (competitorData && competitorData.hasCompetitorData) {
    yPos = checkPageBreak(yPos, 100);
    yPos = addSectionHeader('COMPETITOR COMPARISON', yPos, [139, 92, 246]);
    yPos += 3;
    
    const comparisonData = [
      ['Metric', 'Our Offer', 'Competitor'],
      ['Free Area (%)', `${competitorData.ourFreeAreaPercent}%`, `${competitorData.compFreeAreaPercent}%`],
      ['Corpus (₹/member)', formatCurrency(competitorData.ourCorpus), formatCurrency(competitorData.compCorpus)],
      ['Betterment (₹/member)', formatCurrency(competitorData.ourBetterment), formatCurrency(competitorData.compBetterment)],
      ['Shifting (₹/member)', formatCurrency(competitorData.ourShifting), formatCurrency(competitorData.compShifting)],
      ['Rent - Flat (₹/sft)', formatCurrency(competitorData.ourRentFlat), formatCurrency(competitorData.compRentFlat)],
      ['Rent - Shop (₹/sft)', formatCurrency(competitorData.ourRentShop), formatCurrency(competitorData.compRentShop)],
      ['Project Duration (months)', competitorData.ourDuration, competitorData.compDuration],
      ['Profit Margin (%)', `${formatNumber(competitorData.ourProfitMargin)}%`, `${formatNumber(competitorData.compProfitMargin)}%`],
    ];
    
    doc.autoTable({
      startY: yPos,
      head: [comparisonData[0]],
      body: comparisonData.slice(1),
      theme: 'grid',
      headStyles: { fillColor: [139, 92, 246], ...headStyle },
      bodyStyles: { ...baseTableStyles },
      columnStyles: { 0: { fontStyle: 'bold' }, 1: { halign: 'right', cellWidth: 50 }, 2: { halign: 'right', cellWidth: 50 } },
      margin: tableMargin,
    });
    
    yPos = doc.lastAutoTable.finalY + 8;
  }
  
  // ============ ADD PAGE NUMBERS AND FOOTER ============
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    
    // Footer bar
    doc.setFillColor(...colors.primary);
    doc.rect(0, pageHeight - 10, pageWidth, 10, 'F');
    
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 5, { align: 'center' });
    doc.text(`Redevelopment Feasibility Report – ${formData.societyName || 'Society'}`, pageWidth / 2, pageHeight - 2, { align: 'center' });
  }
  
  return doc;
};

/**
 * Generate PDF and trigger download
 * Uses the shared buildPDFDocument function
 */
export const generateFeasibilityPDF = (
  formData, 
  fsiBreakdown, 
  fsiCosts, 
  daChargesCosts, 
  societyCosts, 
  plData,
  competitorData = null
) => {
  // Build the PDF document using shared function
  const doc = buildPDFDocument(
    formData, 
    fsiBreakdown, 
    fsiCosts, 
    daChargesCosts, 
    societyCosts, 
    plData,
    competitorData
  );
  
  // Generate filename
  const dateStr = new Date().toISOString().split('T')[0];
  const projectName = (formData.projectName || 'Project').replace(/[^a-zA-Z0-9]/g, '_');
  const fileName = `${projectName}_Feasibility_Report_${dateStr}.pdf`;
  
  // Download the PDF
  const pdfBlob = doc.output('blob');
  const blobUrl = URL.createObjectURL(pdfBlob);
  
  const downloadLink = document.createElement('a');
  downloadLink.href = blobUrl;
  downloadLink.download = fileName;
  downloadLink.style.display = 'none';
  
  document.body.appendChild(downloadLink);
  downloadLink.click();
  
  // Cleanup
  setTimeout(() => {
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(blobUrl);
  }, 100);
  
  return fileName;
};

/**
 * Generate PDF as Blob (for sharing via Web Share API)
 * Uses the same shared buildPDFDocument function for consistency
 */
export const generatePDFBlob = (
  formData, 
  fsiBreakdown, 
  fsiCosts, 
  daChargesCosts, 
  societyCosts, 
  plData,
  competitorData = null
) => {
  // Build the PDF document using shared function
  const doc = buildPDFDocument(
    formData, 
    fsiBreakdown, 
    fsiCosts, 
    daChargesCosts, 
    societyCosts, 
    plData,
    competitorData
  );
  
  // Return as blob (same full report)
  return doc.output('blob');
};

export default generateFeasibilityPDF;
